import React, { useEffect, useMemo, useState } from "react"
import {
  faBuilding,
  faCalendar,
  faClose,
  faHashtag,
  faMoneyBill,
  faPercent,
  faTicket,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { EBillingPeriodType } from "../../Enum/EBillingPeriodType"
import { EPlanCouponDiscountType } from "../../Enum/EPlanCouponDiscountType"
import { AdminPlan } from "../../Objects/AdminPlan/AdminPlan"
import { AdminPlanCoupon } from "../../Objects/AdminPlan/AdminPlanCoupon"
import { AdminSavePlanCoupon } from "../../Objects/AdminPlan/AdminSavePlanCoupon"
import { AdminReseller } from "../../Objects/AdminReseller/AdminReseller"
import { useAdminReseller } from "../../hooks/AdminDashboard/useAdminReseller"

interface AdminPlanCouponPopupProps {
  coupon: AdminPlanCoupon | null
  plans: AdminPlan[]
  onClose: () => void
  onSave: (couponId: number | null, request: AdminSavePlanCoupon) => void
}

const NONE_OPTION_VALUE = ""

const toInputDate = (value?: string) => (value ? value.split("T")[0] : "")

const toDatePayload = (value: string) => (value ? new Date(`${value}T12:00:00.000Z`).toISOString() : undefined)

const toDecimalNumber = (value: string) => {
  const normalized = value.replace(",", ".").trim()

  const parsed = normalized ? Number.parseFloat(normalized) : 0

  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

const toOptionalInt = (value: string) => {
  const trimmed = value.trim()

  if (!trimmed) return undefined

  const parsed = Number.parseInt(trimmed, 10)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

export const AdminPlanCouponPopup: React.FC<AdminPlanCouponPopupProps> = ({ coupon, plans, onClose, onSave }) => {
  const { t } = useTranslation("frontend")

  const { handleGetAllResellers } = useAdminReseller()

  const [resellers, setResellers] = useState<AdminReseller[]>([])

  useEffect(() => {
    handleGetAllResellers(setResellers)
  }, [])

  const isEditing = coupon !== null

  const [form, setForm] = useState(new AdminSavePlanCoupon(coupon ?? {}))

  const [validFrom, setValidFrom] = useState(toInputDate(coupon?.validFromUtc))

  const [validUntil, setValidUntil] = useState(toInputDate(coupon?.validUntilUtc))

  const [maxUsageTotal, setMaxUsageTotal] = useState(coupon?.maxUsageTotal ? String(coupon.maxUsageTotal) : "")

  const [maxUsagePerCompany, setMaxUsagePerCompany] = useState(
    coupon?.maxUsagePerCompany ? String(coupon.maxUsagePerCompany) : "",
  )

  const [restrictToCompanyEntityId, setRestrictToCompanyEntityId] = useState(
    coupon?.restrictToCompanyEntityId ? String(coupon.restrictToCompanyEntityId) : "",
  )

  const patch = (data: Partial<AdminSavePlanCoupon>) => setForm(new AdminSavePlanCoupon({ ...form, ...data }))

  const discountTypeOptions = useMemo(
    () =>
      Object.values(EPlanCouponDiscountType)
        .filter((value): value is EPlanCouponDiscountType => typeof value === "number")
        .map((value) => ({ value: String(value), label: (value as number).toEPlanCouponDiscountTypeToString() })),
    [],
  )

  const restrictToPlanOptions = useMemo(
    () => [
      { value: NONE_OPTION_VALUE, label: t("admin_dashboard.plans_and_coupons.coupon_restriction_none") },
      ...plans.map((plan) => ({ value: String(plan.id), label: plan.name })),
    ],
    [plans, t],
  )

  const restrictToPeriodOptions = useMemo(
    () => [
      { value: NONE_OPTION_VALUE, label: t("admin_dashboard.plans_and_coupons.coupon_restriction_none") },
      ...Object.values(EBillingPeriodType)
        .filter((value): value is EBillingPeriodType => typeof value === "number")
        .map((value) => ({ value: String(value), label: (value as number).toEBillingPeriodTypeToString() })),
    ],
    [t],
  )

  const linkedResellerOptions = useMemo(
    () => [
      { value: NONE_OPTION_VALUE, label: t("admin_dashboard.plans_and_coupons.coupon_commercial_referral_none") },
      ...resellers.map((reseller) => ({
        value: String(reseller.resellerId),
        label: `${reseller.name} (${reseller.referralCode})`,
      })),
    ],
    [resellers, t],
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    onSave(
      coupon?.id ?? null,
      new AdminSavePlanCoupon({
        ...form,
        code: form.code.trim().toUpperCase(),
        validFromUtc: toDatePayload(validFrom),
        validUntilUtc: toDatePayload(validUntil),
        maxUsageTotal: toOptionalInt(maxUsageTotal),
        maxUsagePerCompany: toOptionalInt(maxUsagePerCompany),
        restrictToCompanyEntityId: toOptionalInt(restrictToCompanyEntityId),
      }),
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={
                isEditing
                  ? t("admin_dashboard.plans_and_coupons.coupon_edit_title")
                  : t("admin_dashboard.plans_and_coupons.coupon_create_title")
              }
              icon={faTicket}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.button.onClose"
                />
              }
            />

            <form onSubmit={handleSubmit} className="mt-3 space-y-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 p-2">
                <InputField
                  type="text"
                  placeholder={t("admin_dashboard.plans_and_coupons.coupon_code")}
                  onInvalidMessage={t("admin_dashboard.plans_and_coupons.coupon_code_invalid")}
                  icon={faHashtag}
                  value={form.code}
                  required
                  maxLength={INPUT_SIZE.ADMIN_PLAN_COUPON.code}
                  onChange={(event) => patch({ code: event.target.value.toUpperCase() })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.code"
                />

                <GenericDropDown
                  data={discountTypeOptions}
                  initialState={String(form.discountType)}
                  handlerFuncion={(event) => patch({ discountType: Number(event.target.value) as EPlanCouponDiscountType })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.select.discountType"
                />

                <InputField
                  type="number"
                  placeholder={
                    form.discountType === EPlanCouponDiscountType.Percentage
                      ? t("admin_dashboard.plans_and_coupons.coupon_discount_value_percentage")
                      : t("admin_dashboard.plans_and_coupons.coupon_discount_value_fixed")
                  }
                  onInvalidMessage={t("admin_dashboard.plans_and_coupons.coupon_discount_value_invalid")}
                  icon={form.discountType === EPlanCouponDiscountType.Percentage ? faPercent : faMoneyBill}
                  value={String(form.discountValue || "")}
                  required
                  min={0}
                  max={form.discountType === EPlanCouponDiscountType.Percentage ? 100 : undefined}
                  step="0.01"
                  onChange={(event) => patch({ discountValue: toDecimalNumber(event.target.value) })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.discountValue"
                />

                <InputField
                  type="date"
                  placeholder={t("admin_dashboard.plans_and_coupons.coupon_valid_from")}
                  icon={faCalendar}
                  value={validFrom}
                  onChange={(event) => setValidFrom(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.validFrom"
                />

                <InputField
                  type="date"
                  placeholder={t("admin_dashboard.plans_and_coupons.coupon_valid_until")}
                  icon={faCalendar}
                  value={validUntil}
                  onChange={(event) => setValidUntil(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.validUntil"
                />

                <InputField
                  type="number"
                  placeholder={t("admin_dashboard.plans_and_coupons.coupon_max_usage_total")}
                  icon={faUsers}
                  value={maxUsageTotal}
                  min={1}
                  onChange={(event) => setMaxUsageTotal(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.maxUsageTotal"
                />

                <InputField
                  type="number"
                  placeholder={t("admin_dashboard.plans_and_coupons.coupon_max_usage_per_company")}
                  icon={faUsers}
                  value={maxUsagePerCompany}
                  min={1}
                  onChange={(event) => setMaxUsagePerCompany(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.maxUsagePerCompany"
                />

                <InputField
                  type="number"
                  placeholder={t("admin_dashboard.plans_and_coupons.coupon_restrict_to_company")}
                  icon={faBuilding}
                  value={restrictToCompanyEntityId}
                  min={1}
                  onChange={(event) => setRestrictToCompanyEntityId(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.input.restrictToCompany"
                />

                <GenericDropDown
                  data={restrictToPlanOptions}
                  initialState={form.restrictToPlanEntityId ? String(form.restrictToPlanEntityId) : NONE_OPTION_VALUE}
                  handlerFuncion={(event) =>
                    patch({
                      restrictToPlanEntityId: event.target.value ? Number(event.target.value) : undefined,
                    })
                  }
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.select.restrictToPlan"
                />

                <GenericDropDown
                  data={restrictToPeriodOptions}
                  initialState={
                    form.restrictToPeriodType !== undefined ? String(form.restrictToPeriodType) : NONE_OPTION_VALUE
                  }
                  handlerFuncion={(event) =>
                    patch({
                      restrictToPeriodType:
                        event.target.value === NONE_OPTION_VALUE
                          ? undefined
                          : (Number(event.target.value) as EBillingPeriodType),
                    })
                  }
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.select.restrictToPeriod"
                />

                <div className="md:col-span-2">
                  <GenericDropDown
                    data={linkedResellerOptions}
                    initialState={
                      form.linkedResellerEntityId ? String(form.linkedResellerEntityId) : NONE_OPTION_VALUE
                    }
                    handlerFuncion={(event) =>
                      patch({
                        linkedResellerEntityId: event.target.value ? Number(event.target.value) : undefined,
                      })
                    }
                    descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.select.linkedCommercialReferral"
                  />

                  <p className="mt-1 px-1 text-xs text-slate-500">
                    {t("admin_dashboard.plans_and_coupons.coupon_commercial_referral_helper")}
                  </p>
                </div>
              </div>

              <div className="p-2">
                <SimpleToggle
                  value={form.isActive}
                  title={t("admin_dashboard.plans_and_coupons.coupon_is_active")}
                  onChange={(nextValue) => patch({ isActive: nextValue })}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.toggle.isActive"
                />
              </div>

              <div className="flex flex-col-reverse gap-2 p-2 md:flex-row md:justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  onClick={onClose}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.button.cancel"
                >
                  {t("admin_dashboard.plans_and_coupons.cancel")}
                </button>

                <button
                  type="submit"
                  className="rounded-lg border border-orange-500 bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700"
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminPlanCouponPopup.button.save"
                >
                  {t("admin_dashboard.plans_and_coupons.save")}
                </button>
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
